import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Alert, AlertDescription } from './ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Icon } from '@/components/ui/icon';
import { cn } from '@/lib/utils';

interface DomainVerificationProps {
  domain: string;
  onVerificationComplete?: (verified: boolean) => void;
  className?: string;
}

type VerificationMethod = 'dns_txt' | 'dns_cname' | 'file_upload' | 'meta_tag';

interface VerificationInstructions {
  verificationId: string;
  domain: string;
  method: VerificationMethod;
  instructions: string;
  verificationUrl?: string;
  expiresAt: string;
}

interface VerificationStatus {
  domain: string;
  isVerified: boolean;
  verifiedAt?: string;
  currentVerification?: {
    id: string;
    verification_method: VerificationMethod;
    status: string;
    verified_at?: string;
    expires_at: string;
    created_at: string;
  } | null;
}

const methodLabels: Record<VerificationMethod, string> = {
  dns_txt: 'DNS TXT Record',
  dns_cname: 'DNS CNAME Record',
  file_upload: 'File Upload',
  meta_tag: 'HTML Meta Tag',
};

const methodDescriptions: Record<VerificationMethod, string> = {
  dns_txt: 'Add a TXT record to your DNS settings (recommended)',
  dns_cname: 'Add a CNAME record to your DNS settings',
  file_upload: 'Upload a verification file to your website',
  meta_tag: 'Add a meta tag to your homepage HTML',
};

export function DomainVerification({ domain, onVerificationComplete, className }: DomainVerificationProps) {
  const [selectedMethod, setSelectedMethod] = useState<VerificationMethod>('dns_txt');
  const [instructions, setInstructions] = useState<VerificationInstructions | null>(null);
  const [status, setStatus] = useState<VerificationStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load verification status on mount only
  useEffect(() => {
    loadVerificationStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadVerificationStatus = async () => {
    try {
      const response = await fetch(`/api/domain-verification/status?domain=${encodeURIComponent(domain)}`);
      const data = await response.json();
      
      if (response.ok) {
        setStatus(data);
        if (data.isVerified && onVerificationComplete) {
          onVerificationComplete(true);
        }
      } else {
        setError(data.message || 'Failed to load verification status');
      }
    } catch (err) {
      setError('Failed to load verification status');
    }
  };

  const startVerification = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/domain-verification/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          domain,
          method: selectedMethod,
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setInstructions(data);
      } else {
        setError(data.message || 'Failed to start verification');
      }
    } catch (err) {
      setError('Failed to start verification');
    } finally {
      setIsLoading(false);
    }
  };

  const verifyDomain = async () => {
    if (!instructions) return;
    
    setIsVerifying(true);
    setError(null);
    
    try {
      const response = await fetch('/api/domain-verification/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          verificationId: instructions.verificationId,
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Verification successful - reload status and notify parent
        await loadVerificationStatus();
        setInstructions(null);
        if (onVerificationComplete) {
          onVerificationComplete(true);
        }
      } else {
        setError(data.message || 'Verification failed');
      }
    } catch (err) {
      setError('Verification failed');
    } finally {
      setIsVerifying(false);
    }
  };

  const formatInstructions = (text: string) => {
    return text.split('\n').map((line, index) => (
      <div key={index} className={cn(
        line.startsWith('Name:') || line.startsWith('Type:') || line.startsWith('Value:') || line.startsWith('File:') || line.startsWith('Content:') 
          ? 'font-mono text-sm bg-muted px-2 py-1 rounded' 
          : ''
      )}>
        {line}
      </div>
    ));
  };

  if (status?.isVerified) {
    return (
      <div className={className}>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg flex items-center justify-center">
            <img 
              src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`} 
              alt={`${domain} favicon`}
              className="h-8 w-8 rounded-sm"
              onError={(e) => {
                // Fallback to check icon if favicon fails to load
                const parent = (e.target as HTMLImageElement).parentElement;
                if (parent) {
                  parent.innerHTML = '<svg class="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>';
                  parent.className = "h-10 w-10 rounded-full bg-green-100 flex items-center justify-center";
                }
              }} 
            />
          </div>
          <div>
            <p className="font-medium">Domain Verified</p>
            <p className="text-sm text-muted-foreground">
              {domain} was verified on {status.verifiedAt ? new Date(status.verifiedAt).toLocaleDateString() : 'Unknown date'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <Icon name="alert-01" size={16} />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!instructions ? (
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Choose verification method</Label>
              <Tabs value={selectedMethod} onValueChange={(value) => setSelectedMethod(value as VerificationMethod)}>
                <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4">
                  <TabsTrigger value="dns_txt">DNS TXT</TabsTrigger>
                  <TabsTrigger value="dns_cname">DNS CNAME</TabsTrigger>
                  <TabsTrigger value="file_upload">File Upload</TabsTrigger>
                  <TabsTrigger value="meta_tag">Meta Tag</TabsTrigger>
                </TabsList>
                
                {Object.entries(methodDescriptions).map(([method, description]) => (
                  <TabsContent key={method} value={method} className="mt-3">
                    <p className="text-sm text-muted-foreground">{description}</p>
                  </TabsContent>
                ))}
              </Tabs>
            </div>

            <Button onClick={startVerification} disabled={isLoading} className="w-full">
              {isLoading ? (
                <>
                  <Icon name="refresh" size={16} className="animate-spin mr-2" />
                  Starting verification...
                </>
              ) : (
                <>
                  <Icon name="play" size={16} className="mr-2" />
                  Start Verification
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Badge variant="outline">
                {methodLabels[instructions.method]}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setInstructions(null)}
              >
                <Icon name="x" size={16} />
              </Button>
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-medium">Follow these instructions:</Label>
              <div className="bg-muted p-4 rounded-lg space-y-2 text-sm">
                {formatInstructions(instructions.instructions)}
              </div>
              
              {instructions.verificationUrl && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="external-link" size={14} />
                  <span>Verification URL: </span>
                  <a 
                    href={instructions.verificationUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="font-mono text-blue-600 hover:underline"
                  >
                    {instructions.verificationUrl}
                  </a>
                </div>
              )}
            </div>

            <Separator />

            <div className="flex gap-2">
              <Button onClick={verifyDomain} disabled={isVerifying} className="flex-1">
                {isVerifying ? (
                  <>
                    <Icon name="refresh" size={16} className="animate-spin mr-2" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <Icon name="check" size={16} className="mr-2" />
                    Verify Domain
                  </>
                )}
              </Button>
              <Button variant="outline" onClick={loadVerificationStatus}>
                <Icon name="refresh" size={16} />
              </Button>
            </div>

            <p className="text-xs text-muted-foreground">
              Expires: {new Date(instructions.expiresAt).toLocaleDateString()}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}